'use client'

import { ResponsiveLineChart } from '@ethena/shared-ui/src/components/Dashboards/ResponsiveLineChart/ResponsiveLineChart'
import { LANDING_CHART_COLORS } from '@ethena/shared-ui/src/constants/chart'
import { cn } from '@ethena/shared-ui/utils'

import { differenceInMonths } from 'date-fns'
import { ReactNode } from 'react'
import { Payload } from 'recharts/types/component/DefaultTooltipContent'

import { SavingsTimeScale } from '@/hooks/useSavingsData'

interface SavingsChartProps {
  data: Record<string, number | null>[] | undefined
  timeScale: SavingsTimeScale
  setTimeScale: (scale: SavingsTimeScale) => void
}

export const SavingsChart = ({ data, setTimeScale, timeScale }: SavingsChartProps) => {
  const shouldShowEntry = (isEstimated: boolean, isTransitionOrAfter: boolean) => {
    return !((!isEstimated && isTransitionOrAfter) || (isEstimated && !isTransitionOrAfter))
  }

  const renameEstimatedSeries = (name: string | undefined) => {
    if (!name) return name
    if (name === 'Estimated sUSDe') return 'sUSDe'
    if (name === 'Estimated Treasuries') return 'Treasuries'
    if (name === 'Estimated USDT/USDC') return 'USDT/USDC'
    return name
  }

  const entryPayloadFilter = (entry: Payload<number, string>) => {
    if (entry.value === null || !data) return null

    const newEntry = { ...entry } as Payload<number, string> & { extraInfo?: ReactNode }

    if ('date' in newEntry.payload) {
      const lastActualPoint = data.find(point => point.sUSDe !== null && point['Estimated sUSDe'] !== null)
      const isTransitionOrAfter = lastActualPoint?.date ? newEntry.payload.date >= lastActualPoint.date : false
      const isEstimated = entry.name?.startsWith('Estimated')

      if (!shouldShowEntry(isEstimated ?? false, isTransitionOrAfter)) {
        return null
      }

      if (isTransitionOrAfter && isEstimated) {
        newEntry.name = renameEstimatedSeries(newEntry.name)
      }
    }

    return newEntry
  }

  const legendItems = [
    { color: 'bg-text-accent', isDashed: false, label: 'sUSDe' },
    { color: 'bg-[#C2C2C0] bg-opacity-50', isDashed: false, label: 'Treasuries' },
    { color: 'bg-[#6f6f6f]', isDashed: true, label: 'USDT/USDC' },
  ] as const

  const formatXAxis = (timestamp: number) => {
    const startDate = data?.[0]?.date

    if (!startDate) return ''
    const monthDiff = differenceInMonths(timestamp, startDate)
    return `Month ${monthDiff + 1}`
  }

  return (
    <ResponsiveLineChart
      data={data}
      extraStyles="w-full h-full !bg-none !bg-transparent !rounded-none border-none !backdrop-blur-none"
      containerClassName="w-full h-full"
      height="100%"
      width="100%"
      title=""
      header={
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="flex flex-row flex-wrap gap-6">
            {legendItems.map(({ color, isDashed, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                {isDashed ? (
                  <div className="w-[10px] flex items-center">
                    <div className={`h-[2px] w-[2px] mr-[2px] ${color}`} />
                    <div className={`h-[2px] w-[2px] mr-[2px] ${color}`} />
                    <div className={`h-[2px] w-[2px] ${color}`} />
                  </div>
                ) : (
                  <div className={`h-[2px] w-[10px] ${color}`} />
                )}
                <p className="text-sm text-text-muted">{label}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-row flex-wrap gap-1 items-center justify-end">
            {['End of 2024', '+1Y', '+5Y'].map(scale => (
              <button
                key={scale}
                onClick={() => setTimeScale(scale as SavingsTimeScale)}
                className={cn(
                  'w-[64px] h-[30px] text-sm flex items-center justify-center transition-all duration-300 overflow-hidden',
                  timeScale === scale
                    ? 'bg-gradient-to-t from-cblue-400/80 from-40% via-transparent via-[97%] p-[1px] relative border border-[#34343A]'
                    : 'border border-[#34343A] opacity-70',
                  scale === 'End of 2024' ? 'rounded-l-full w-[100px]' : '',
                  scale === '+5Y' ? 'rounded-r-full' : '',
                  scale === '+1Y' ? 'rounded-[4px]' : '',
                )}
              >
                <div
                  className={cn(
                    'w-full h-full flex items-center justify-center bg-[#17181c]',
                    scale === 'End of 2024' ? 'rounded-l-full' : '',
                    scale === '+5Y' ? 'rounded-r-full' : '',
                    scale === '+1Y' ? 'rounded-[4px]' : '',
                    timeScale === scale
                      ? 'relative after:absolute after:inset-0 after:transition-opacity after:duration-300 after:[background:radial-gradient(90%_80%_at_50%_100%,#DAE3F233_0,#667A9B00_100%)] after:opacity-100'
                      : '',
                  )}
                >
                  {scale}
                </div>
              </button>
            ))}
          </div>
        </div>
      }
      margin={{ bottom: 32, left: 10, right: 34, top: 16 }}
      dataKeys={['sUSDe', 'Treasuries', 'USDT/USDC', 'Estimated USDT/USDC', 'Estimated sUSDe', 'Estimated Treasuries']}
      dashedKeys={['Estimated sUSDe', 'Estimated Treasuries', 'Estimated USDT/USDC']}
      strokeColors={{
        'Estimated sUSDe': LANDING_CHART_COLORS.sUSDe,
        'Estimated Treasuries': LANDING_CHART_COLORS.Treasuries,
        'Estimated USDT/USDC': LANDING_CHART_COLORS['USDT/USDC'],
        sUSDe: LANDING_CHART_COLORS.sUSDe,
        Treasuries: LANDING_CHART_COLORS.Treasuries,
        'USDT/USDC': LANDING_CHART_COLORS['USDT/USDC'],
      }}
      entryPayloadFilter={entryPayloadFilter}
      connectNulls={true}
      hideYAxis
      hideLegend
      xAxisOverrides={{
        axisLine: { stroke: '#09090b' },
        dy: 10,
        stroke: '#303032',
        tickFormatter: formatXAxis,
      }}
      showLandingTooltip
      showGradientReferenceLine
      strokeWidth={2}
      animateWhenInView
      chartResetKey={timeScale}
    />
  )
}
